<script setup lang="ts">
import type { Notification } from '@/composables/useToast'
// @ts-expect-error: no type declaration
import toastPreset from '@/presets/nomic/toast'
import { computed } from 'vue'

// The body of every toast the application raises. Supplying Toast's `message`
// template is what lets a notification carry a link, and one template has to
// serve them all: a second Toast for the linked ones would be a second stack,
// and two stacks in the same corner sit on top of each other.
interface Props {
  message: Notification
}

const { message } = defineProps<Props>()

// Matched as closely as the vendored set allows to the glyph PrimeVue draws per
// severity, so unstyled Toast keeps reading the way it did before this template
// took over from it. `warn` and `error` share one: `circle-exclamation` is
// vendored in sharp-regular only, and the two are told apart by the colouring
// the preset applies to the container anyway.
const icons: Readonly<Record<string, string>> = Object.freeze({
  success: 'fa-sharp fa-solid fa-circle-check',
  info: 'fa-sharp fa-solid fa-circle-info',
  warn: 'fa-sharp fa-solid fa-triangle-exclamation',
  error: 'fa-sharp fa-solid fa-triangle-exclamation',
})

const icon = computed(() => icons[message.severity ?? 'info'] ?? icons['info'])
</script>

<template>
  <!-- Supplying Toast's `message` template bypasses the preset's `icon`,
       `text`, `summary` and `detail` pass-throughs, so those four are read
       straight off the preset here instead, and follow it when it changes;
       the container's severity colouring still comes from the preset, and the
       close button sits outside this template and is untouched.

       The link below is invented: no toast in the repository carried an action
       before this one, so nothing settles what one looks like or what it says
       (#663). -->
  <Icon :icon="icon" :class="toastPreset.icon.class" data-testid="toast-notification-icon" />
  <div :class="toastPreset.text.class">
    <span :class="toastPreset.summary.class">{{ message.summary }}</span>
    <div :class="toastPreset.detail.class">{{ message.detail }}</div>
    <router-link
      v-if="message.route"
      :to="message.route"
      class="v-paragraph-sm underline inline-block mt-3"
      data-testid="toast-notification-link"
    >
      View report
    </router-link>
  </div>
</template>
